import { useRef } from 'react';
import { PageHero } from '../components/ui/PageHero.jsx';
import { Section, Wrap } from '../components/ui/Section.jsx';
import { useActiveStep } from '../hooks/useActiveStep.js';
import styles from './ProcessPage.module.css';
import { CtaBanner } from '../components/ui/CtaBanner.jsx';
import { ProcessStep } from '../components/ui/ProcessStep.jsx';
import { processStages } from '../data/content.js';

export const meta = {
  title: 'Our Process — Abode Custom Homes',
  description:
    'How Abode Custom Homes builds a custom home in Bloomington, IL: from first consultation through design, permitting, construction, and move-in.',
};

/** Anchor id for a stage, shared by the rail link and its target. */
const stageId = (num) => `stage-${num}`;

export default function ProcessPage() {
  const stagesRef = useRef(null);
  const activeStep = useActiveStep(stagesRef, '[id^="stage-"]', processStages.length);

  return (
    <>
      <PageHero
        eyebrow="How We Build"
        lines={['A clear, honest path from', 'first conversation to move-in.']}
        lede="No black boxes. Every Abode project follows the same five stages, so you always know what's happening, what's next, and what it costs."
      />

      <Section>
        <Wrap>
          <div className={styles.layout}>
            {/* The index. Five stages is a short enough list to show whole,
                which is the point: the lede promises you always know what is
                happening and what is next, and this is the part of the page
                that actually delivers it. It also gives the wide half of the
                screen something to do besides stay empty.

                Hidden below 1000px, where the stages already read well in a
                single column and a rail would only take space from them. */}
            <nav className={styles.rail} aria-label="Process stages">
              <ol className={styles.railList}>
                {processStages.map((stage, i) => (
                  <li key={stage.num}>
                    <a
                      href={`#${stageId(stage.num)}`}
                      className={styles.railItem}
                      aria-current={i === activeStep ? 'true' : undefined}
                    >
                      <span className={styles.railNum}>{stage.num}</span>
                      <span className={styles.railName}>{stage.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className={styles.stages} ref={stagesRef}>
              {processStages.map((stage) => (
                <ProcessStep
                  key={stage.num}
                  id={stageId(stage.num)}
                  num={stage.num}
                  title={stage.title}
                  meta={stage.meta}
                >
                  {stage.body}
                </ProcessStep>
              ))}
            </div>
          </div>
        </Wrap>
      </Section>

      <Section tone="dark">
        <Wrap>
          <CtaBanner
            eyebrow="Ready When You Are"
            lines={['Tell us about', 'your project.']}
            action="Start a Conversation"
            onDark
          />
        </Wrap>
      </Section>
    </>
  );
}
